import { DateTime } from 'luxon';
import { ActType } from 'shell/constants/act-type';
import { ISTAT_MILANO } from 'shell/constants/istat-milano';
import {
  alphanumericRegex,
  idActRegex,
  nameSurnameMoreCharactersAccentsRegex,
} from 'shell/constants/regex';
import { ActManagementForm } from 'shell/view-models/act-form';
import { DocumentDigital } from 'shell/view-models/act-form';
import { SubscriptionGainCitizenshipFormVM } from 'subscription/view-models/subscription-gain-citizenship';
import * as yup from 'yup';

const validationSchemaCitizenshipNew = yup
  .object()
  .shape({
    // subscriptionBirthData: yup.object().shape({
    //   familyAttribute: yup.string().required('Campo obbligatorio'),
    // }),
    editDataControl: yup.object().shape({
      dateValidity: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .required('Campo obbligatorio')
        .max(
          DateTime.local().plus({ days: 1 }),
          'La data non può essere successiva alla data odierna'
        ),
      // assume: yup.string().required('Campo obbligatorio'),
      // requested: yup.string().required('Campo obbligatorio'),
      // subscribeTo: yup.string().required('Campo obbligatorio'),
      flag: yup.string().required('Campo obbligatorio'),
    }),
    generality: yup.object().shape({
      fiscalCode: yup.string().nullable().notRequired(),
      // .matches(fiscalCodeRegex, 'Formato non valido'),
      surname: yup
        .string()
        .when('withoutSurname', {
          is: false,
          then: yup.string().required('Campo obbligatorio'),
          otherwise: yup.string(),
        })
        .matches(
          nameSurnameMoreCharactersAccentsRegex,
          'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
        ),
      withoutSurname: yup.boolean(),
      name: yup
        .string()
        .when('withoutName', {
          is: false,
          then: yup.string().required('Campo obbligatorio'),
          otherwise: yup.string(),
        })
        .matches(
          nameSurnameMoreCharactersAccentsRegex,
          'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
        ),
      withoutName: yup.boolean(),
      gender: yup.string().required('Campo obbligatorio'),
      aire: yup.string().required('Campo obbligatorio'),
      birthDate: yup
        .date()
        .nullable()
        .typeError('Data non valida')
        .required('Campo obbligatorio')
        .max(DateTime.now(), 'La data deve essere minore della data odierna'),
      // generality italian form
      italian: yup
        .object()
        .nullable()
        .when('isForeign', {
          is: false || undefined,
          then: yup.object().shape({
            municipality: yup
              .object()
              .nullable()
              .shape({
                description: yup.string().required('Campo obbligatorio'),
              }),
            // province: yup.string().required('Campo obbligatorio'),
          }),
          otherwise: yup.object().shape({
            municipality: yup.object().nullable().shape({
              description: yup.string().notRequired().nullable(),
            }),
            province: yup.string().notRequired().nullable(),
          }),
        }),
      foreign: yup
        .object()
        .nullable()
        .when('isForeign', {
          is: true,
          then: yup.object().shape({
            municipality: yup
              .object()
              .nullable()
              .shape({
                description: yup.string().required('Campo obbligatorio'),
              }),
            state: yup
              .object()
              .nullable()
              .shape({
                value: yup.string().required('Campo obbligatorio'),
              }),
          }),
          otherwise: yup.object().shape({
            municipality: yup.object().nullable().shape({
              description: yup.string().notRequired().nullable(),
            }),
            state: yup.object().nullable().shape({
              value: yup.string().notRequired().nullable(),
            }),
          }),
        }),
      // Other fields generality
      exceptionalBirthPlace: yup.string(),
      municipalityIdentifier: yup.string(),
      istatCode: yup.string().required('Campo obbligatorio'),
      // anprIdentifier: yup.string().required('Campo obbligatorio'),
      isForeign: yup.boolean(),
    }),
    birthdayDocument: yup
      .object()
      .nullable()
      .shape({
        actType: yup.string(),
        digital: yup
          .object()
          .nullable()
          .when('actType', {
            is: ActType.AttoDigitale,
            otherwise: yup.object().nullable(),
            then: yup
              .object()
              .nullable()
              .shape({
                idAnsc: yup
                  .string()
                  .nullable()
                  .matches(idActRegex, 'Formato non corretto')
                  .test((value) => {
                    if (value) {
                      const [year] = value.split('-');
                      const isValid = year ? Number.parseInt(year) >= 2022 : true;

                      return isValid
                        ? true
                        : new yup.ValidationError(
                            "L'idAnsc deve avere anno maggiore al 2022",
                            undefined,
                            'birthdayDocument.digital.idAnsc'
                          );
                    }
                    return true;
                  })
                  .required('Campo richiesto'),
                documentDate: yup
                  .date()
                  .nullable()
                  .notRequired()
                  .typeError('Formato non valido')
                  .required('Campo richiesto'),
                municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
              })
              .test((values) => {
                const castedValues = values as unknown as DocumentDigital;

                if (
                  castedValues.municipality?.id &&
                  castedValues.istatCode &&
                  castedValues.municipality?.id === castedValues.istatCode
                ) {
                  return true;
                }
                return new yup.ValidationError([
                  new yup.ValidationError(
                    'Non coincidono i due codici ISTAT',
                    undefined,
                    'birthdayDocument.digital.municipality'
                  ),
                  new yup.ValidationError(
                    'Non coincidono i due codici ISTAT',
                    undefined,
                    'birthdayDocument.digital.istatCode'
                  ),
                ]);
              }),
          }),
        paper: yup
          .object()
          .nullable()
          .when('actType', {
            is: ActType.AttoCartaceo,
            otherwise: yup.object().nullable(),
            then: yup.object().shape({
              actDate: yup
                .date()
                .nullable()
                .max(
                  DateTime.local().plus({ days: 1 }),
                  'La data non può essere successiva alla data odierna'
                )
                .typeError('Data non valida'),
              transcribed: yup.string().nullable(),
              actMunicipality: yup.object().nullable().shape({}).required('Il campo è richiesto'),
              actYear: yup.number().nullable().required('Campo richiesto'),
              actNumber: yup
                .number()
                .nullable()
                .required('Campo richiesto')
                .typeError('Sono ammesse solamente cifre'),
              actPart: yup
                .string()
                .nullable()
                .required('Campo richiesto')
                .test(
                  'part-ammisible-values',
                  'Gli unici valori ammessi sono 1 o 2',
                  (value) => ['1', '2'].indexOf(value || '') >= 0
                ),
              actSeries: yup
                .string()
                .nullable()
                .required('Campo richiesto')
                .test(
                  'part-ammisible-values',
                  'Gli unici valori ammessi sono A, B o C',
                  (value) => ['A', 'B', 'C'].indexOf(value || '') >= 0
                ),
              actVolume: yup
                .string()
                .nullable()
                .when('actMunicipality.description', {
                  is: (description: string) => {
                    return (description || '').toLocaleLowerCase() === 'milano';
                  },
                  then: yup
                    .string()
                    // .required('Campo obbligatorio')
                    .matches(/^R[0-9]{2}$/, {
                      message:
                        'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                    }),
                }),
            }),
          }),
      })
      .test('Check birthday document', '', (values) => {
        const castedValues = values as ActManagementForm;
        if (
          castedValues?.paper?.actMunicipality?.id === ISTAT_MILANO &&
          !castedValues.paper?.actVolume
        ) {
          return new yup.ValidationError(
            'Il campo è obbligatorio',
            undefined,
            'birthdayDocument.paper.volume'
          );
        }
        return true;
      }),
    citizenship: yup.object().shape({
      state: yup.object().shape({}).nullable().required('Campo obbligatorio'),
      note: yup.string(),
      validity: yup.date().nullable().required('Campo obbligatorio').typeError('Data non valida'),
    }),
    fatherhood: yup
      .object()
      .default(null)
      .nullable()
      .shape({
        generality: yup.object().shape({
          surname: yup
            .string()
            .when('withoutSurname', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameMoreCharactersAccentsRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutSurname: yup.boolean(),
          name: yup
            .string()
            .when('withoutName', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameMoreCharactersAccentsRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutName: yup.boolean(),
          gender: yup.string(),
          aire: yup.string(),
          birthDate: yup.date().nullable().typeError('Data non valida'),
          // generality italian form
          // italian: yup.object().when('isForeign', {
          //   is: false || undefined,
          //   then: yup.object().shape({
          //     municipality: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         description: yup.string().required('Campo obbligatorio'),
          //       }),
          //     // province: yup.string().required('Campo obbligatorio'),
          //   }),
          //   otherwise: yup.object().shape({
          //     municipality: yup.object().nullable().shape({
          //       description: yup.string().notRequired().nullable(),
          //     }),
          //     province: yup.string().notRequired().nullable(),
          //   }),
          // }),
          // foreign: yup.object().when('isForeign', {
          //   is: true,
          //   then: yup.object().shape({
          //     municipality: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         description: yup.string().required('Campo obbligatorio'),
          //       }),
          //     state: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         value: yup.string().required('Campo obbligatorio'),
          //       }),
          //   }),
          //   otherwise: yup.object().shape({
          //     municipality: yup.object().nullable().shape({
          //       description: yup.string().notRequired().nullable(),
          //     }),
          //     state: yup.object().nullable().shape({
          //       value: yup.string().notRequired().nullable(),
          //     }),
          //   }),
          // }),
          // Other fields generality
          exceptionalBirthPlace: yup
            .string()
            .notRequired()
            .nullable()
            .matches(alphanumericRegex, 'Il campo non è valido'),
          municipalityIdentifier: yup
            .string()
            .notRequired()
            .nullable()
            .matches(alphanumericRegex, 'Il campo non è valido'),
          // istatCode: yup.string().required('Campo obbligatorio'),
          // anprIdentifier: yup.string().required('Campo obbligatorio'),
          isForeign: yup.boolean(),
        }),
      }),
    motherhood: yup
      .object()
      .default(null)
      .nullable()
      .shape({
        generality: yup.object().shape({
          surname: yup
            .string()
            .when('withoutSurname', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameMoreCharactersAccentsRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutSurname: yup.boolean(),
          name: yup
            .string()
            .when('withoutName', {
              is: false || undefined,
              then: yup.string().required('Campo obbligatorio'),
              otherwise: yup.string(),
            })
            .matches(
              nameSurnameMoreCharactersAccentsRegex,
              'Non sono ammessi caratteri speciali tranne -(trattino) e . (punto)'
            ),
          withoutName: yup.boolean(),
          gender: yup.string(),
          aire: yup.string(),
          birthDate: yup.date().nullable().typeError('Data non valida'),
          // generality italian form
          // italian: yup.object().when('isForeign', {
          //   is: false || undefined,
          //   then: yup.object().shape({
          //     municipality: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         description: yup.string().required('Campo obbligatorio'),
          //       }),
          //     // province: yup.string().required('Campo obbligatorio'),
          //   }),
          //   otherwise: yup.object().shape({
          //     municipality: yup.object().nullable().shape({
          //       description: yup.string().notRequired().nullable(),
          //     }),
          //     province: yup.string().notRequired().nullable(),
          //   }),
          // }),
          // foreign: yup.object().when('isForeign', {
          //   is: true,
          //   then: yup.object().shape({
          //     municipality: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         description: yup.string().required('Campo obbligatorio'),
          //       }),
          //     state: yup
          //       .object()
          //       .nullable()
          //       .shape({
          //         value: yup.string().required('Campo obbligatorio'),
          //       }),
          //   }),
          //   otherwise: yup.object().shape({
          //     municipality: yup.object().nullable().shape({
          //       description: yup.string().notRequired().nullable(),
          //     }),
          //     state: yup.object().nullable().shape({
          //       value: yup.string().notRequired().nullable(),
          //     }),
          //   }),
          // }),
          // Other fields generality
          exceptionalBirthPlace: yup.string(),
          municipalityIdentifier: yup.string(),
          // istatCode: yup.string().required('Campo obbligatorio'),
          // anprIdentifier: yup.string().required('Campo obbligatorio'),
          isForeign: yup.boolean(),
        }),
      }),
  })
  .test(
    'birthdayDocument.documentDate',
    'La data non può essere minore della Data di nascita',
    (values) => {
      const castedValues = values as unknown as SubscriptionGainCitizenshipFormVM;

      if (
        castedValues.generality?.birthDate &&
        castedValues.birthdayDocument?.paper?.actDate &&
        castedValues.generality?.birthDate > castedValues.birthdayDocument?.paper?.actDate
      ) {
        return new yup.ValidationError(
          'La data non può essere minore della Data di nascita',
          undefined,
          'birthdayDocument.paper.actDate'
        );
      }

      return false;
    }
  )
  .test('citizenship.validity', 'La data deve essere uguale alla Data di nascita', (values) => {
    const castedValues = values as unknown as SubscriptionGainCitizenshipFormVM;

    if (
      castedValues.generality?.birthDate &&
      castedValues.citizenship?.validity &&
      castedValues.generality?.birthDate > castedValues.citizenship?.validity
    ) {
      return new yup.ValidationError(
        'La data deve essere uguale o maggiore alla Data di nascita',
        undefined,
        'citizenship.validity'
      );
    }

    const today = DateTime.now();

    if (castedValues.citizenship?.validity && castedValues.citizenship?.validity > today) {
      return new yup.ValidationError(
        'La data deve essere minore uguale alla data di oggi',
        undefined,
        'citizenship.validity'
      );
    }

    return false;
  })
  .test('birthdayDocument.year', "L'anno non può essere minore della Data di nascita", (values) => {
    const castedValues = values as unknown as SubscriptionGainCitizenshipFormVM;

    const birthYear = castedValues.generality?.birthDate
      ? castedValues.generality?.birthDate.year
      : undefined;

    if (
      birthYear &&
      castedValues.birthdayDocument?.paper?.actYear &&
      birthYear > Number.parseInt(castedValues.birthdayDocument?.paper?.actYear, 10)
    ) {
      return new yup.ValidationError(
        "L'anno non può essere minore della Data di nascita",
        undefined,
        'birthdayDocument.paper.actYear'
      );
    }

    return false;
  })
  .test('Check all shape', '', (values) => {
    if (values.editDataControl.flag !== '1' && !values.generality.fiscalCode) {
      return new yup.ValidationError('Il campo è obbligatorio', undefined, 'generality.fiscalCode');
    }

    return false;
  });

export default validationSchemaCitizenshipNew;
